import type React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { ExtensionParams, Parameters } from '@atlaskit/editor-common/extensions';
import type { RendererAppearance } from '@atlaskit/renderer';

import type { ProductDependenciesProviderProps } from './controllers/use-product-dependencies';
import type { SpaceDensity } from './utils/hub-space-density/types';
import type { HubTypography } from './utils/hub-typography';

export type ExtensionParameters<T extends Parameters> = {
	contentId: string;
	extensionNode?: ExtensionParams<T>;
};

export enum ButtonStyle {
	DEFAULT = 'default',
	ALTERNATIVE_1 = 'alternative_1',
}

export enum CornerStyle {
	ROUNDED = 'rounded',
	SQUARED = 'squared',
}

export enum Height {
	MEDIUM = 'medium',
	SMALL = 'small',
	LARGE = 'large',
}

// TODO: Centralize all Alignment enums across extensions to use this enum
export enum Alignment {
	LEFT = 'left',
	RIGHT = 'right',
	CENTER = 'center',
}

// TODO: Centralize all Orientation enums across extensions to use this enum
export enum Orientation {
	ROW = 'row',
	STACKED = 'stacked',
}

export enum CardLayout {
	FOLDERS = 'folders',
	ACCORDIONS = 'accordions',
}

export enum CommonLayout {
	FRAME_ABOVE_IMAGE = 'frame-above-image',
	SPLIT_IMAGE_RIGHT = 'split-image-right',
	SPLIT_IMAGE_LEFT = 'split-image-left',
	FULL_IMAGE_BACKGROUND = 'full-image-background',
	NO_IMAGE_BACKGROUND = 'no-image-background',
}

export enum FeedbackType {
	CARDS = 'Cards',
	CAROUSEL = 'Carousel',
	COMPANY_HUB = 'Company hub',
	CUSTOMIZE_NAVIGATION = 'Customize Navigation',
	LINKS_MENU = 'Links menu',
	SPOTLIGHT = 'Spotlight',
	SECTION = 'Section',
	CUSTOM_SEARCH = 'Custom search',
}

export enum ExtensionMode {
	RENDERER = 'renderer',
	EDITOR = 'editor',
}

export type HubElement = {
	description: MessageDescriptor;
	extensionKey: 'cards' | 'carousel' | 'spotlight'; // Unique extension identifier
	getNodeAdf?: (intl: IntlShape) => ADFEntity; // ADF that we insert to replace the placeholder
	Image?: React.ReactNode;
	layouts?: HubElementLayout[];
	PreviewComponent?: React.ReactNode; // Component that renders preview of the extension
	title: MessageDescriptor;
};

export type HubElementLayout = {
	description: MessageDescriptor;
	getNodeAdf?: (intl: IntlShape) => ADFEntity;
	getPreviewComponent?: (intl: IntlShape) => React.ReactNode;
	label: MessageDescriptor;
	value: any;
};

export const LOG_OUT = 'epic.logout.LOG_OUT';

export interface LogOutAction {
	type: typeof LOG_OUT;
}

export type HubContextType = {
	branding: {
		colors: {
			/**
			 * Optional accent (secondary) brand color. Persisted on the BE as
			 * `secondaryColor` on `HelpCenterBrandingColors`; mapped to `accent`
			 * at the bootstrap boundary. Optional because pre-v2 hubs and non-
			 * help-center hosts (Confluence Company Hub, CSM) don't populate it.
			 */
			accent?: string;
			primary: string;
		};
		/**
		 * Hub-wide corner radius (in whole pixels) applied to extension cards
		 * and banners. Persisted on the BE as `HelpCenterBranding.cornerRadius`;
		 * mapped at the bootstrap boundary. Optional — undefined for hubs that
		 * have not set a value, in which case consumers fall back to 8 px.
		 */
		cornerRadius?: number;
		logo: {
			fileId: string;
			url: string;
		};
		/**
		 * Admin-chosen space density (DEFAULT / COMFORTABLE / BREEZY) that will
		 * control the vertical rhythm between hub elements. Persisted on the BE
		 * as `HelpCenterBranding.spaceDensity` (the `HelpCenterSpaceDensity`
		 * enum) and surfaced verbatim at the bootstrap boundary. Optional —
		 * undefined for pre-space-density hubs and non-help-center hosts
		 * (Confluence Company Hub, CSM).
		 */
		spaceDensity?: SpaceDensity;
		/**
		 * Admin-chosen heading + body fonts. Persisted on the BE as
		 * `HelpCenterBranding.typography { headingFont, bodyFont }`; mapped to
		 * the FE `HubTypography` shape at the bootstrap boundary. Optional —
		 * undefined for pre-typography hubs and non-help-center hosts
		 * (Confluence Company Hub, CSM), in which case the framework falls back
		 * to Atlassian Sans for both.
		 */
		typography?: HubTypography;
	};
	cloudId: string;
	cloudUrl: string;
	hubMediaConfig: {
		asapIssuer: string;
		mediaCollectionName: string;
		mediaToken: string;
		mediaUrl: string;
	};
	/**
	 * @private
	 * @deprecated Avoid introducing product-specific code into platform.
	 */
	hubType: string;
	isInPreviewMode?: boolean;
	/**
	 * Media config scoped to the currently rendered page. This is populated by
	 * the `Hub` component (via `hubQuery`) and is therefore not available from
	 * `HubBootstrap` alone — consumers that read it must be rendered as
	 * descendants of `Hub`.
	 */
	pageMediaConfig?: {
		asapIssuer: string;
		mediaCollectionName: string;
		mediaToken: string;
		mediaUrl: string;
	};
} & HubAppProps;

export type ContainerStyling = React.CSSProperties;

export type HubFeatureFlags = {
	[key: string]: boolean;
};

export type HubAppProps = {
	actions: {
		onLogoutClick: () => LogOutAction;
	};
	appearance?: RendererAppearance;
	containerStyling?: ContainerStyling;
	env: {
		/**
		 * @private
		 * @deprecated Do not use. Use the root-level `cloudId` from `HubContextType` instead.
		 */
		cloudId?: string;
		helpCenterAri: string;
		helpCenterId: string;
		hoistedProjectKey: string;
		hubEditMode?: boolean;
		jiraDisplayUrl: string;
		relativeBaseUrl: string;
	};
	featureFlags?: HubFeatureFlags;
	/**
	 * When true, the hub shell renders without the navigation bar.
	 * Intended for embedded/headless experiences (e.g. iOS app embedding the support site).
	 */
	headerless?: boolean;
	/**
	 * Whether the tenant is entitled to Premium, supplied by the host. Tri-state:
	 * `undefined` means "unknown" and never locks a feature.
	 */
	isPremiumEntitled?: boolean;
	layout?: {
		bannerText: string;
		isAnonymousRoute: boolean;
		showBanner: boolean;
	};
	/**
	 * Optional wrapper for product extension renderers. It is applied around the
	 * extension component on both editor and renderer paths, so hosts can inject
	 * extra context (for example, help-center's Forge/Connect renderer bridge).
	 */
	productExtensionsDependenciesProvider?: React.ComponentType<ProductDependenciesProviderProps>;
	renderAvatarMenuOptions?: () => React.JSX.Element;
	renderCustomNavActions?: (iconColor?: string) => React.JSX.Element | null;
	renderRightNavActions?: () => React.JSX.Element;
	showPageNameAsHubDocumentTitle?: boolean;
	user: {
		avatarSrc: string;
		displayName: string;
		email: string;
		id: string;
		isJiraAdmin: boolean;
	};
};

export enum RenderVariant {
	MANUAL = 'manual',
	DYNAMIC = 'dynamic',
}

export enum SegmentationTargets {
	ALL = 'all',
	EACH = 'each',
}

export enum ITEM_FIELDS {
	CARD_ID = 'cardId',
	TITLE = 'title',
	IS_TITLE_USER_EDITED = 'isTitleUserEdited',
	DESCRIPTION = 'description',
	IS_DESCRIPTION_USER_EDITED = 'isDescriptionUserEdited',
	IS_DESCRIPTION_SHOWN = 'isDescriptionShown',
	LABEL = 'label',
	IS_LABEL_HIDDEN = 'isLabelHidden',
	LINK = 'link',
	IS_LINK_USER_EDITED = 'isLinkUserEdited',
	IMAGE_SRC = 'imageSrc',
	CONFLUENCE_PAGE_ID = 'confluencePageId',
	IMAGE_ALT_TEXT = 'imageAltText',
	SEGMENTATION_TARGETS = 'segmentationTarget',
	AUDIENCE_SEGMENTATION_GROUP_IDS = 'audienceSegmentationGroupIds', // group preferences stored via this field when segmentationTarget is EACH
	LINK_RESOURCE_TYPE = 'linkResourceType',
	BACKGROUND_COLOR = 'backgroundColor',
	TEXT_COLOR = 'textColor',
	LABEL_COLOR = 'labelColor',
	LAYOUT = 'layout',
	IS_BUTTON_SHOWN = 'isButtonShown',
	BUTTON_TEXT = 'buttonText',
	BUTTON_STYLE = 'buttonStyle',
	HEIGHT = 'height',
}

export type Item = {
	[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]?: string[];
	[ITEM_FIELDS.BACKGROUND_COLOR]?: string;
	[ITEM_FIELDS.BUTTON_STYLE]?: ButtonStyle;
	[ITEM_FIELDS.BUTTON_TEXT]?: string;
	[ITEM_FIELDS.CARD_ID]: string;
	[ITEM_FIELDS.CONFLUENCE_PAGE_ID]?: string;
	[ITEM_FIELDS.DESCRIPTION]?: string;
	[ITEM_FIELDS.HEIGHT]?: Height;
	[ITEM_FIELDS.IMAGE_ALT_TEXT]?: string;
	[ITEM_FIELDS.IMAGE_SRC]?: string;
	[ITEM_FIELDS.IS_BUTTON_SHOWN]?: boolean;
	[ITEM_FIELDS.IS_DESCRIPTION_SHOWN]?: boolean;
	[ITEM_FIELDS.IS_DESCRIPTION_USER_EDITED]?: boolean;
	[ITEM_FIELDS.IS_LABEL_HIDDEN]?: boolean;
	[ITEM_FIELDS.IS_LINK_USER_EDITED]?: boolean;
	[ITEM_FIELDS.IS_TITLE_USER_EDITED]?: boolean;
	[ITEM_FIELDS.LABEL_COLOR]?: string;
	[ITEM_FIELDS.LABEL]?: string;
	[ITEM_FIELDS.LAYOUT]?: CommonLayout;
	[ITEM_FIELDS.LINK_RESOURCE_TYPE]?: string;
	[ITEM_FIELDS.LINK]: string;
	[ITEM_FIELDS.SEGMENTATION_TARGETS]?: SegmentationTargets;
	[ITEM_FIELDS.TEXT_COLOR]?: string;
	[ITEM_FIELDS.TITLE]?: string;
};

export type NavigationBarApiResponse =
	| {
			readonly backgroundColor: string | null | undefined;
			readonly borderColor: string | null | undefined;
			readonly borderType: string | null | undefined;
			readonly cornerRadius: number | null | undefined;
			readonly padding: boolean | null | undefined;
			readonly positionType: string | null | undefined;
			readonly textColor: string | null | undefined;
	  }
	| null
	| undefined;
